export function redirect(url, openInBlankPage = true) {
  if (typeof window !== 'undefined') {
    openInBlankPage ? window.open(url, '_blank') : window.open(url);
  }
}

export function getUrlParameter(name) {
  if (typeof window === 'undefined') return null;

  const urlParams = new URLSearchParams(window.location.search);
  return urlParams.get(name);
}

/**
 * Captures attribution parameters from URL and stores in localStorage
 */
function captureAttribution() {
  if (typeof window === 'undefined') return {};

  // Get URL parameters
  const urlParams = new URLSearchParams(window.location.search);
  const attributionParams = {};

  // List of parameters to capture
  const paramsToCapture = [
    'utm_source',
    'utm_medium',
    'utm_campaign',
    'utm_content',
    'utm_term',
    'ref',
    'influencer',
    'channel',
  ];

  // Capture parameters if they exist
  paramsToCapture.forEach((param) => {
    const value = urlParams.get(param);
    if (value) {
      attributionParams[param] = value;
    }
  });

  // Only store if we have at least one attribution parameter
  if (Object.keys(attributionParams).length > 0) {
    localStorage.setItem(
      'cypher_attribution',
      JSON.stringify(attributionParams)
    );
  }

  return attributionParams;
}

/**
 * Gets stored attribution data
 */
function getStoredAttribution() {
  if (typeof window === 'undefined') return {};

  try {
    const stored = localStorage.getItem('cypher_attribution');
    return stored ? JSON.parse(stored) : {};
  } catch (e) {
    console.error('Error reading attribution data:', e);
    return {};
  }
}

/**
 * Gets a specific attribution parameter from stored data
 * Falls back to URL if not in stored data
 */
export function getAttributionParameter(name) {
  if (typeof window === 'undefined') return '';

  // First try to get from stored attribution
  const attribution = getStoredAttribution();
  if (attribution && attribution[name]) {
    return attribution[name];
  }

  // Fall back to URL parameter if not in storage
  return getUrlParameter(name) || '';
}

/**
 * Unified event tracking function that automatically includes attribution data
 */
export function trackEvent(eventName, parameters = {}) {
  // Get attribution data (tries URL first, then stored data)
  const captured = captureAttribution();
  const attribution =
    Object.keys(captured).length > 0 ? captured : getStoredAttribution();

  // Create event data object with attribution data directly (not nested)
  const eventData = {
    event: eventName,
    ...parameters,
    // Spread attribution data directly into the event
    ...(Object.keys(attribution).length > 0 && attribution),
  };

  // Push to dataLayer
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push(eventData);
}

/**
 * Legacy compatibility function that converts old ReactGA format to new dataLayer format
 */
export function logAnalytics({ category, action, label, parameters = {} }) {
  trackEvent(action, {
    event_category: category,
    event_label: label,
    ...parameters,
  });
}

/**
 * Initialize analytics and capture initial attribution
 * Call this function early in your app's lifecycle
 */
export function initializeAnalytics() {
  window.dataLayer = window.dataLayer || [];

  // Capture initial attribution if any
  captureAttribution();

  // Initialize GA4 configuration
  window.dataLayer.push({
    event: 'ga4_init',
    // Include attribution data in initialization if available
    attribution: getStoredAttribution(),
  });
}

/**
 * Constructs a URL with attribution parameters
 * @param {string} baseUrl - The base URL to add parameters to
 * @param {boolean} preserveExisting - Whether to preserve existing parameters in the base URL
 * @returns {string} - The URL with attribution parameters
 */
export function constructUrlWithAttribution(baseUrl, preserveExisting = true) {
  if (typeof window === 'undefined') return baseUrl;

  try {
    // Parse the base URL to preserve any existing parameters if needed
    const url = new URL(baseUrl);
    const existingParams = preserveExisting
      ? url.searchParams
      : new URLSearchParams();

    // Get attribution params
    const attributionParams = [
      'utm_source',
      'utm_medium',
      'utm_campaign',
      'utm_content',
      'utm_term',
      'ref',
      'influencer',
      'channel',
    ];

    // Add attribution parameters from stored data
    attributionParams.forEach((param) => {
      const value = getAttributionParameter(param);
      if (value && !existingParams.has(param)) {
        existingParams.set(param, value);
      }
    });

    // Build the final URL
    const searchParams = existingParams.toString();
    const baseUrlWithoutParams = baseUrl.split('?')[0];

    return searchParams
      ? `${baseUrlWithoutParams}?${searchParams}`
      : baseUrlWithoutParams;
  } catch (error) {
    console.error('Error constructing URL with attribution:', error);
    return baseUrl;
  }
}
